import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Form, Button, Row, Col, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import Message from '../components/Message'
import Loader from '../components/Loader'
import FormContainer from '../components/FormContainer'
import { forgotPassword, resetPasswordState } from '../slices/userPasswordSlice'

const ForgotPasswordScreen = ({location}) => {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const dispatch = useDispatch()
  
  const userPassword = useSelector((state) => state.userPassword)
  const { loading, successForgot, error } = userPassword || {}

  const redirect = location.search ? location.search.split('=')[1] : '/'

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Sanitize email input
  const sanitizeEmail = (value) => {
    return value
      .toLowerCase()
      .trim()
      .slice(0, 254) // Max email length
      .replace(/[<>]/g, '') // Remove potential HTML characters
  }

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = sanitizeEmail(e.target.value)
    setEmail(value)
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  // Reset form after successful submission
  useEffect(() => {
    if (successForgot) {
      const timer = setTimeout(() => {
        dispatch(resetPasswordState())
        setEmail('')
        setCaptchaToken('')
        setCaptchaError('')
        setCountdown(60) // 60 second cooldown
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successForgot, dispatch])

  // Countdown timer for rate limiting feedback
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const submitHandler = (e) => {
    e.preventDefault()
    
    // Reset errors
    setEmailError('')
    setCaptchaError('')

    // Validate email
    if (!email) {
      setEmailError('Email address is required')
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    // Validate captcha
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification')
      return
    }

    // Dispatch with email and captcha
    dispatch(forgotPassword({ email, grecaptcha: captchaToken }))
  }

  const isSubmitDisabled = 
    loading || 
    !email || 
    !captchaToken || 
    !!emailError || 
    countdown > 0

  return (
    <FormContainer>
      <h1>Forgot Password</h1>
      
      {error && <Message variant='danger'>{error}</Message>}
      
      {successForgot && (
        <Alert variant='success'>
          <Alert.Heading>✓ Email Sent!</Alert.Heading>
          <p>
            If an account exists with this email address, you will receive password reset instructions shortly. 
            Please check your inbox and spam folder.
          </p>
        </Alert>
      )}
      
      {countdown > 0 && (
        <Alert variant='info'>
          Please wait {countdown} seconds before requesting another password reset.
        </Alert>
      )}
      
      {loading && <Loader />}
      
      {!successForgot && (
        <>
          <h4>Can't remember your password?</h4>
          <p>
            No worries, enter the email associated with your account, and we will send a password reset link to that email address.
          </p>
          
          <Form onSubmit={submitHandler}>
            <Form.Group controlId='email' className='mb-3'>
              <Form.Label>Account Email Address</Form.Label>
              <Form.Control
                type='email'
                placeholder='Enter email'
                value={email}
                onChange={handleEmailChange}
                isInvalid={!!emailError}
                maxLength={254}
                autoComplete='email'
              />
              {emailError && (
                <Form.Control.Feedback type='invalid'>
                  {emailError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group controlId='captcha' className='mb-3'>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                onChange={(token) => {
                  setCaptchaToken(token || '')
                  setCaptchaError('')
                }}
              />
              {captchaError && (
                <small className='text-danger'>{captchaError}</small>
              )}
            </Form.Group>

            <Button 
              type='submit' 
              variant='primary' 
              disabled={isSubmitDisabled}
              className='mb-3'
            >
              {countdown > 0 ? `Wait ${countdown}s` : 'Send Reset Email'}
            </Button>
          </Form>
        </>
      )}

      <Row className='py-3'>
        <Col>
          Know your Password?{' '}
          <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>
            Back to login
          </Link>
        </Col>
      </Row>
    </FormContainer>
  )
}

export default ForgotPasswordScreen